import { AuditAction, ShutdownReason, World, WorldAudit } from '../types';

/** Bytes → "412 MB" / "1.20 GB". */
export const fmtBytes = (bytes: number | undefined | null): string => {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const v = bytes / Math.pow(1024, i);
  return `${i >= 3 ? v.toFixed(2) : Math.round(v)} ${units[i]}`;
};

/** Absolute local date-time string. */
/** Bytes per second: "1.2 MB/s". */
export const fmtRate = (bytesPerSecond: number | undefined | null): string => `${fmtBytes(bytesPerSecond)}/s`;

export const fmtDateTime = (value: string | number | undefined | null): string => {
  if (value == null || value === '') return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
};

/** Relative time: "just now", "11 min ago", "yesterday", "5 days ago"… */
export const timeAgo = (value: string | number | undefined | null): string => {
  if (value == null || value === '') return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const diff = Date.now() - d.getTime();
  const s = Math.round(diff / 1000);
  if (s < 45) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const days = Math.round(h / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.round(days / 7)} week${days < 14 ? '' : 's'} ago`;
  if (days < 365) return `${Math.round(days / 30)} month${days < 60 ? '' : 's'} ago`;
  return d.toLocaleDateString();
};

/** Time left until a future moment: "under a minute", "12 min", "1 h 05 min"; null once it has passed. */
export const timeUntil = (value: string | number | undefined | null): string | null => {
  if (value == null || value === '') return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const diff = d.getTime() - Date.now();
  if (diff <= 0) return null;
  const m = Math.ceil(diff / 60000);
  if (m < 1) return 'under a minute';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest === 0 ? `${h} h` : `${h} h ${String(rest).padStart(2, '0')} min`;
};

/** Why a running server is going to shut down automatically. */
export const shutdownReasonLabel = (reason: ShutdownReason | undefined): string => {
  switch (reason) {
    case 'EMPTY_SERVER':
      return 'no players';
    case 'OWNER_ABSENT':
      return 'owner away';
    default:
      return '';
  }
};

// ===== World status =====
export type StatusKey = 'online' | 'offline' | 'starting' | 'restoring' | 'shutting' | 'error';

export interface StatusInfo {
  key: StatusKey;
  text: string;
  shortText: string;
  pill: string;
  dot: string;
  /** Server is running and can be stopped / killed. */
  active: boolean;
  /** Transitional state – actions are disabled. */
  busy: boolean;
}

const STATUS_MAP: Record<string, Omit<StatusInfo, 'pill' | 'dot'>> = {
  ACTIVE: { key: 'online', text: 'Online', shortText: 'Online', active: true, busy: false },
  INACTIVE: { key: 'offline', text: 'Offline', shortText: 'Offline', active: false, busy: false },
  OFFLINE: { key: 'offline', text: 'Offline', shortText: 'Offline', active: false, busy: false },
  LOADING: { key: 'starting', text: 'Starting up…', shortText: 'Starting…', active: false, busy: true },
  CREATING: { key: 'starting', text: 'Creating world…', shortText: 'Creating…', active: false, busy: true },
  RESTORING: { key: 'restoring', text: 'Restoring backup', shortText: 'Restoring', active: false, busy: true },
  SHUTTING_DOWN: { key: 'shutting', text: 'Shutting down…', shortText: 'Stopping…', active: false, busy: true },
  ERROR: { key: 'error', text: 'Error', shortText: 'Error', active: false, busy: false },
};

const PILL_CLASS: Record<StatusKey, string> = {
  online: 'pill--online',
  offline: 'pill--offline',
  starting: 'pill--warn',
  restoring: 'pill--restore',
  shutting: 'pill--warn',
  error: 'pill--error',
};

export const statusInfo = (status: string | undefined | null): StatusInfo => {
  const base = STATUS_MAP[(status || '').toUpperCase()] || {
    key: 'offline' as StatusKey,
    text: status || 'Unknown',
    shortText: status || 'Unknown',
    active: false,
    busy: false,
  };
  return { ...base, pill: PILL_CLASS[base.key], dot: `dot--${base.key}` };
};

/** Generation label: "vanilla · seed 8277991" / "superflat" / "void world". */
export const genLabel = (w: World): string => {
  const g = (w.generationType || 'VANILLA').toUpperCase();
  if (g === 'FLAT') return 'superflat';
  if (g === 'VOID') return 'void world';
  return w.seed ? `vanilla · seed ${w.seed}` : 'vanilla';
};

/** Block icon used to represent a world, derived from its generation type. */
export const worldBlock = (w: World): string => {
  const g = (w.generationType || 'VANILLA').toUpperCase();
  if (g === 'FLAT') return 'oak';
  if (g === 'VOID') return 'diamond';
  return 'grass';
};

// ===== Audits =====
const AUDIT_LABEL: Record<AuditAction, string> = {
  PLAYER_JOINED: 'Player joined',
  PLAYER_LEFT: 'Player left',
  WORLD_BACKUP_CREATED: 'Backup created',
  WORLD_BACKUP_REQUESTED: 'Backup requested',
  WORLD_BACKUP_RESTORED: 'Backup restored',
  SERVER_OFFLINE: 'Server offline',
  SERVER_CRASHED: 'Server crashed',
  WORLD_LOADED: 'World loaded',
  WORLD_SETTINGS_CHANGED: 'Settings changed',
  ADMIN_ACTION: 'Admin action',
  WORLD_CREATED: 'World created',
  WORLD_DELETED: 'World deleted',
  WORLD_RENAMED: 'World renamed',
  TRUST_ADDED: 'Trusted player added',
  TRUST_REMOVED: 'Trusted player removed',
  WARP_CREATED: 'Warp created',
  WARP_DELETED: 'Warp deleted',
  WARP_UPDATED: 'Warp updated',
};

/** Verb phrase used after the actor's name: "<b>name</b> joined the world". */
const AUDIT_VERB: Record<AuditAction, string> = {
  PLAYER_JOINED: 'joined the world',
  PLAYER_LEFT: 'left the world',
  WORLD_BACKUP_CREATED: 'created a backup',
  WORLD_BACKUP_REQUESTED: 'requested a backup',
  WORLD_BACKUP_RESTORED: 'restored a backup',
  SERVER_OFFLINE: 'took the server offline',
  SERVER_CRASHED: 'had the server crash',
  WORLD_LOADED: 'loaded the world',
  WORLD_SETTINGS_CHANGED: 'changed world settings',
  ADMIN_ACTION: 'performed an admin action',
  WORLD_CREATED: 'created the world',
  WORLD_DELETED: 'deleted the world',
  WORLD_RENAMED: 'renamed the world',
  TRUST_ADDED: 'added a trusted player',
  TRUST_REMOVED: 'removed a trusted player',
  WARP_CREATED: 'created a warp',
  WARP_DELETED: 'deleted a warp',
  WARP_UPDATED: 'updated a warp',
};

const humanise = (action: string) =>
  action
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase());

export const auditLabel = (action: string): string =>
  AUDIT_LABEL[action as AuditAction] || humanise(action);

export const auditVerb = (action: string): string =>
  AUDIT_VERB[action as AuditAction] || humanise(action).toLowerCase();

/** Audits that are hidden from the player-facing activity feed. */
export const isVisibleAudit = (a: WorldAudit): boolean => a.action !== 'ADMIN_ACTION';

// ===== Logs =====
export type LogLevel = 'info' | 'warn' | 'err' | 'ok';

export const logLevel = (message: string): LogLevel => {
  if (/\b(ERROR|SEVERE|FATAL|Exception)\b/.test(message)) return 'err';
  if (/\b(WARN|WARNING)\b/.test(message)) return 'warn';
  if (/\b(Done \(|joined the game)\b/.test(message)) return 'ok';
  return 'info';
};

/** Short uuid for fallbacks: "3b9a3618…". */
export const shortId = (id: string | null | undefined): string =>
  id ? `${id.slice(0, 8)}…` : '-';
